import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import './SignaturePad.scss';

const SignaturePad = forwardRef(({ value, disabled, onChange }, ref) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const clearCanvas = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Preenche o fundo com branco
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Redesenha a linha guia após limpar
    ctx.setLineDash([5, 5]);
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = 1;
    ctx.beginPath();

    // Linha horizontal
    const lineY = rect.height * 0.85;
    ctx.moveTo(20, lineY);
    ctx.lineTo(rect.width - 20, lineY);

    ctx.stroke();
    ctx.setLineDash([]);

    if (onChange) {
      onChange(null);
    }
  };

  useImperativeHandle(ref, () => ({
    getSignatureData: () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Obtém os dados do canvas para encontrar os limites da assinatura
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;

      let minX = canvas.width;
      let minY = canvas.height;
      let maxX = 0;
      let maxY = 0;

      // Encontra os limites da assinatura (pixels não brancos)
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const index = (y * canvas.width + x) * 4;
          const r = pixels[index];
          const g = pixels[index + 1];
          const b = pixels[index + 2];
          const a = pixels[index + 3];

          // Se o pixel não é branco (tem algum traço)
          if (a > 0 && (r < 250 || g < 250 || b < 250)) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }

      // Adiciona uma margem ao redor da assinatura
      const margin = 20;
      minX = Math.max(0, minX - margin);
      minY = Math.max(0, minY - margin);
      maxX = Math.min(canvas.width, maxX + margin);
      maxY = Math.min(canvas.height, maxY + margin);

      // Calcula as dimensões da área recortada
      const cropWidth = maxX - minX;
      const cropHeight = maxY - minY;

      // Cria um canvas de exportação com tamanho otimizado
      const exportCanvas = document.createElement('canvas');
      const exportCtx = exportCanvas.getContext('2d', {
        alpha: false,
        willReadFrequently: false
      });

      // Define tamanho fixo mas proporcional (600px de largura, altura proporcional)
      const targetWidth = 600;
      const aspectRatio = cropHeight / cropWidth;
      const targetHeight = Math.round(targetWidth * aspectRatio);

      exportCanvas.width = targetWidth;
      exportCanvas.height = targetHeight;

      // Preenche com fundo branco
      exportCtx.fillStyle = '#ffffff';
      exportCtx.fillRect(0, 0, targetWidth, targetHeight);

      // Configurações para máxima qualidade
      exportCtx.imageSmoothingEnabled = true;
      exportCtx.imageSmoothingQuality = 'high';

      // Desenha apenas a área recortada da assinatura, redimensionada
      exportCtx.drawImage(
        canvas,
        minX, minY, cropWidth, cropHeight, // Origem (área recortada)
        0, 0, targetWidth, targetHeight // Destino (tamanho final)
      );

      // Retorna com qualidade máxima (1.0) em PNG
      return exportCanvas.toDataURL('image/png', 1.0);
    },
    clearCanvas: clearCanvas,
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', {
      alpha: true,
      desynchronized: true, // Melhor performance em mobile
      willReadFrequently: false
    });

    // Ajusta o canvas para a resolução correta
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    ctx.scale(dpr, dpr);

    // Configurações para melhor qualidade de renderização
    ctx.imageSmoothingEnabled = false; // Linhas mais nítidas

    // Preenche o fundo com branco
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Se já tem uma assinatura salva, desenha ela
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = value;
    } else {

      // Desenha linha pontilhada guia para assinatura
      ctx.setLineDash([5, 5]); // Cria linha pontilhada (5px traço, 5px espaço)
      ctx.strokeStyle = '#cccccc'; // Cor cinza clara
      ctx.lineWidth = 1;
      ctx.beginPath();

      // Linha horizontal
      const lineY = rect.height * 0.85; // Linha a 85% da altura
      ctx.moveTo(20, lineY); // Começa com margem de 20px
      ctx.lineTo(rect.width - 20, lineY); // Termina com margem de 20px

      ctx.stroke();

      // Reseta o estilo de linha para desenho normal
      ctx.setLineDash([]);
    }
  }, [value]);

  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;

    // Calcula as coordenadas relativas ao canvas
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    return { x, y };
  };

  const startDrawing = (e) => {
    if (disabled) return;
    e.preventDefault(); // Previne scroll no mobile

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e, canvas);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault(); // Previne scroll no mobile

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e, canvas);

    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3; // Linha um pouco mais grossa para melhor visualização
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round'; // Junções suaves entre segmentos
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  return (
    <div className="signature-pad">
      <canvas
        ref={canvasRef}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        className={`signature-pad__canvas ${
          disabled
            ? 'signature-pad__canvas--disabled'
            : 'signature-pad__canvas--enabled'
        }`}
      />
    </div>
  );
});

export default SignaturePad;
